import { useMemo, useState } from 'react';
import type { UpstreamModelMappingRecord } from '@nadoutong/contracts';
import { useChannelsList } from '../api/useChannels.ts';
import { useModelsList, useModelProtocols } from '../api/useModels.ts';
import { useCreateModelMapping, useDeleteModelMapping, useModelMappingsList, useUpdateModelMapping } from '../api/useModelMappings.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

interface Props {
  readonly upstreamId: string;
  readonly mode?: 'editable' | 'legacy';
}

type MappingTableRow = {
  readonly vendor: string;
  readonly channelSlug: string;
  readonly channelName: string;
  readonly model: string;
  readonly displayName: string;
  readonly protocol: string;
  readonly mapping: UpstreamModelMappingRecord | null;
};

export function ModelMappingsCard({ upstreamId, mode = 'editable' }: Props): JSX.Element {
  const t = useT();
  const { data: mappings, isLoading: loadingMappings } = useModelMappingsList(upstreamId);
  const { data: channels, isLoading: loadingChannels } = useChannelsList();
  const { data: allModels, isLoading: loadingModels } = useModelsList();
  const createMut = useCreateModelMapping(upstreamId);
  const deleteMut = useDeleteModelMapping(upstreamId);
  const updateMut = useUpdateModelMapping(upstreamId);
  const toast = useToast();
  const legacyMode = mode === 'legacy';

  const uniqueVendors = useMemo(() => {
    const vs = new Set<string>();
    for (const ch of channels ?? []) vs.add(ch.vendor);
    for (const mapping of mappings ?? []) vs.add(mapping.vendor);
    return [...vs].sort();
  }, [channels, mappings]);

  const [vendorFilter, setVendorFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  const filteredChannels = useMemo(() => {
    if (vendorFilter.length === 0) return channels ?? [];
    return (channels ?? []).filter((ch) => ch.vendor === vendorFilter);
  }, [channels, vendorFilter]);

  const mappingIndex = useMemo(() => {
    const map = new Map<string, UpstreamModelMappingRecord>();
    for (const m of mappings ?? []) {
      map.set(`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`, m);
    }
    return map;
  }, [mappings]);

  const rows = useMemo(() => {
    const vendor = vendorFilter.length > 0 ? vendorFilter : null;
    const channelSlug = channelFilter.length > 0 ? channelFilter : null;
    const models = (allModels ?? []).filter((m) => {
      if (m.deleted_at !== null) return false;
      if (m.shelf_status !== 'listed') return false;
      if (vendor !== null && m.vendor !== vendor) return false;
      return true;
    });

    const chs = channelSlug !== null
      ? filteredChannels.filter((ch) => ch.slug === channelSlug)
      : filteredChannels;

    const result: MappingTableRow[] = [];

    if (legacyMode) {
      for (const mapping of mappings ?? []) {
        if (vendor !== null && mapping.vendor !== vendor) continue;
        if (channelSlug !== null && mapping.channel !== channelSlug) continue;
        const channel = (channels ?? []).find(
          (candidate) => candidate.vendor === mapping.vendor && candidate.slug === mapping.channel,
        );
        const model = (allModels ?? []).find(
          (candidate) => candidate.vendor === mapping.vendor && candidate.model === mapping.model,
        );
        result.push({
          vendor: mapping.vendor,
          channelSlug: mapping.channel,
          channelName: channel?.display_name ?? mapping.channel,
          model: mapping.model,
          displayName: model?.display_name ?? mapping.model,
          protocol: mapping.protocol,
          mapping,
        });
      }
      result.sort((a, b) => {
        if (a.vendor !== b.vendor) return a.vendor.localeCompare(b.vendor);
        if (a.channelSlug !== b.channelSlug) return a.channelSlug.localeCompare(b.channelSlug);
        return a.model.localeCompare(b.model);
      });
      return result;
    }

    for (const m of models) {
      const vendorChannels = chs.filter((ch) => ch.vendor === m.vendor);
      for (const ch of vendorChannels) {
        const key = `${m.vendor}/${ch.slug}/${m.model}/anthropic`;
        result.push({
          vendor: m.vendor,
          channelSlug: ch.slug,
          channelName: ch.display_name,
          model: m.model,
          displayName: m.display_name ?? m.model,
          protocol: 'anthropic',
          mapping: mappingIndex.get(key) ?? null,
        });
      }
    }

    result.sort((a, b) => {
      if (a.vendor !== b.vendor) return a.vendor.localeCompare(b.vendor);
      if (a.channelSlug !== b.channelSlug) return a.channelSlug.localeCompare(b.channelSlug);
      return a.model.localeCompare(b.model);
    });
    return result;
  }, [allModels, channels, filteredChannels, channelFilter, vendorFilter, mappingIndex, legacyMode, mappings]);

  const isLoading = loadingMappings || loadingChannels || loadingModels;

  return (
    <div className="rounded-xl bg-surface-container p-6" data-testid="model-mappings-card">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
            {legacyMode ? t('mappings.legacy_title') : t('mappings.title')}
          </h3>
          {legacyMode ? (
            <p className="mt-1 text-xs leading-5 text-outline">
              {t('mappings.legacy_subtitle')}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={vendorFilter}
            onChange={(e) => { setVendorFilter(e.currentTarget.value); setChannelFilter(''); }}
            className="rounded-lg bg-surface-container-lowest px-2 py-1 text-[11px] text-on-surface"
          >
            <option value="">{t('mappings.filter.all_vendors')}</option>
            {uniqueVendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.currentTarget.value)}
            className="rounded-lg bg-surface-container-lowest px-2 py-1 text-[11px] text-on-surface"
          >
            <option value="">{t('mappings.filter.all_channels')}</option>
            {filteredChannels.map((ch) => <option key={ch.id} value={ch.slug}>{ch.display_name}</option>)}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-surface-container-low" />)}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-outline" data-testid="mappings-empty">
          {legacyMode ? t('mappings.legacy.empty') : t('mappings.empty')}
        </p>
      ) : (
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-left" data-testid="mappings-table">
            <thead className="sticky top-0 z-10 bg-surface-container">
              <tr className="bg-surface-container-high">
                <th scope="col" className="w-28 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
                  {legacyMode ? t('mappings.legacy.col.status') : ''}
                </th>
                <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{t('mappings.col.vendor')}</th>
                <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{t('mappings.col.channel')}</th>
                <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{t('mappings.col.model')}</th>
                <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{t('mappings.col.upstream_name')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <MappingRow
                  key={`${row.vendor}/${row.channelSlug}/${row.model}/${row.protocol}`}
                  row={row}
                  upstreamId={upstreamId}
                  zebra={i % 2 === 0}
                  mode={mode}
                  createMut={createMut}
                  deleteMut={deleteMut}
                  updateMut={updateMut}
                  toast={toast}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MappingRow({
  row,
  upstreamId,
  zebra,
  mode,
  createMut,
  deleteMut,
  updateMut,
  toast,
  t,
}: {
  readonly row: {
    vendor: string;
    channelSlug: string;
    channelName: string;
    model: string;
    displayName: string;
    protocol: string;
    mapping: UpstreamModelMappingRecord | null;
  };
  readonly upstreamId: string;
  readonly zebra: boolean;
  readonly mode: 'editable' | 'legacy';
  readonly createMut: ReturnType<typeof useCreateModelMapping>;
  readonly deleteMut: ReturnType<typeof useDeleteModelMapping>;
  readonly updateMut: ReturnType<typeof useUpdateModelMapping>;
  readonly toast: ReturnType<typeof useToast>;
  readonly t: ReturnType<typeof useT>;
}): JSX.Element {
  const enabled = row.mapping !== null;
  const legacyMode = mode === 'legacy';
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(row.mapping?.upstream_model_name ?? row.model);
  const busy = createMut.isPending || deleteMut.isPending || updateMut.isPending;

  function handleToggle(): void {
    if (enabled) {
      deleteMut.mutate(
        { vendor: row.vendor, channel: row.channelSlug, model: row.model, protocol: row.protocol },
        { onError: (err) => toast.error(coerceErrorMessage(err)) },
      );
    } else {
      createMut.mutate(
        { vendor: row.vendor, channel: row.channelSlug, model: row.model, protocol: row.protocol, upstream_model_name: nameValue },
        { onError: (err) => toast.error(coerceErrorMessage(err)) },
      );
    }
  }

  function commitName(): void {
    const trimmed = nameValue.trim();
    if (trimmed.length === 0 || !enabled || row.mapping === null) {
      setEditingName(false);
      return;
    }
    if (trimmed === row.mapping.upstream_model_name) {
      setEditingName(false);
      return;
    }
    updateMut.mutate(
      { vendor: row.vendor, channel: row.channelSlug, model: row.model, protocol: row.protocol, body: { upstream_model_name: trimmed } },
      {
        onSuccess: () => setEditingName(false),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  return (
    <tr className={`${zebra ? 'bg-surface-container' : 'bg-surface-container-low'} transition-colors hover:bg-surface-bright`}>
      <td className="px-3 py-2">
        {legacyMode ? (
          <span className="inline-flex rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold text-outline">
            {t('mappings.legacy.status.mapped')}
          </span>
        ) : (
          <input
            type="checkbox"
            checked={enabled}
            onChange={handleToggle}
            disabled={busy}
            className="h-4 w-4 accent-primary"
          />
        )}
      </td>
      <td className="px-3 py-2 text-[12px] font-mono text-on-surface-variant">{row.vendor}</td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
          {row.channelName}
        </span>
      </td>
      <td className="px-3 py-2 text-[13px] font-semibold text-on-surface">{row.displayName}</td>
      <td className="px-3 py-2">
        {enabled ? (
          legacyMode ? (
            <span className="text-[12px] font-mono text-primary">
              {row.mapping?.upstream_model_name ?? '—'}
            </span>
          ) : editingName ? (
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.currentTarget.value)}
              onBlur={commitName}
              onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setEditingName(false); }}
              disabled={busy}
              className="w-full rounded bg-surface-container-lowest px-2 py-1 text-[12px] font-mono text-primary border-0 border-b border-outline-variant focus:border-primary focus:outline-none"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={() => { setNameValue(row.mapping?.upstream_model_name ?? row.model); setEditingName(true); }}
              className="text-[12px] font-mono text-primary hover:underline"
            >
              {row.mapping?.upstream_model_name ?? '—'}
            </button>
          )
        ) : (
          <span className="text-[12px] text-outline">—</span>
        )}
      </td>
    </tr>
  );
}
